import { handleSubjectInputPaste } from "@/lib/pastePlainText";
import {
  clampTicketSubject,
  SUBJECT_MAX_LENGTH,
  subjectMaxLengthError,
} from "@sd/lib/ticketFormConstants";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { sd, SD_BASE } from "@sd/lib/sdPaths";
import { Header } from "@/components/layout/Header";
import { ArrowLeft, User, Building, Search, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const categories = [
  { value: "hardware", label: "Hardware Issue" },
  { value: "software", label: "Software Issue" },
  { value: "network", label: "Network/Connectivity" },
  { value: "access", label: "Access Request" },
  { value: "email", label: "Email/Communication" },
  { value: "security", label: "Security Concern" },
  { value: "other", label: "Other" },
];

const users = [
  {
    id: "u1",
    name: "John Smith",
    email: "john.smith@company.com",
    company: "Global Finance",
  },
  {
    id: "u2",
    name: "Sarah Connor",
    email: "sarah.connor@techstart.com",
    company: "TechStart Inc",
  },
  {
    id: "u3",
    name: "Mike Johnson",
    email: "mike.j@acmecorp.com",
    company: "Acme Corp",
  },
  {
    id: "u4",
    name: "Emily Davis",
    email: "emily.davis@healthcareplus.com",
    company: "HealthCare Plus",
  },
];

const NewTicket = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<"requester" | "details">("requester");
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<(typeof users)[0] | null>(
    null
  );
  const [formData, setFormData] = useState({
    subject: "",
    category: "",
    priority: "P3",
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredUsers = users.filter(
    user =>
      user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const handleSubmit = async () => {
    const subjectTrimmed = formData.subject.trim();
    if (!subjectTrimmed) {
      toast.error("Subject is required.");
      return;
    }
    if (subjectTrimmed.length > SUBJECT_MAX_LENGTH) {
      toast.error(subjectMaxLengthError());
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    toast.success("Ticket created successfully!", {
      description: "The ticket has been assigned to the appropriate queue.",
    });
    navigate(`${SD_BASE}/`);
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      <Header />

      <ScrollArea className="flex-1">
        <div className="mx-auto w-full max-w-2xl p-4 sm:p-6 lg:p-8">

          {/* Back + Title */}
          <div className="mb-5 sm:mb-7">
            <button
              onClick={() =>
                step === "details"
                  ? setStep("requester")
                  : navigate(`${SD_BASE}/`)
              }
              className="mb-3 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              {step === "details"
                ? "Back to requester selection"
                : "Back to tickets"}
            </button>
            <h1 className="text-xl font-bold text-foreground sm:text-2xl">
              Create New Ticket
            </h1>
            <p className="mt-1 text-sm text-muted-foreground sm:text-base">
              {step === "requester"
                ? "Select or add a requester for this ticket"
                : "Fill in the ticket details"}
            </p>
          </div>

          {/* Progress Steps — always horizontal */}
          <div className="mb-6 flex items-center gap-2 sm:mb-8 sm:gap-4">
            {/* Step 1 */}
            <div
              className={cn(
                "flex items-center gap-2 text-sm font-medium",
                step === "requester" ? "text-primary" : "text-emerald-600"
              )}
            >
              <div
                className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold sm:h-8 sm:w-8 sm:text-sm",
                  step === "requester"
                    ? "bg-primary text-primary-foreground"
                    : "bg-emerald-500 text-white"
                )}
              >
                {step === "details" ? <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" /> : "1"}
              </div>
              <span className="text-xs font-semibold sm:text-sm">Requester</span>
            </div>

            {/* Divider */}
            <div className="h-px flex-1 bg-border" />

            {/* Step 2 */}
            <div
              className={cn(
                "flex items-center gap-2 text-sm font-medium",
                step === "details" ? "text-primary" : "text-muted-foreground"
              )}
            >
              <div
                className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold sm:h-8 sm:w-8 sm:text-sm",
                  step === "details"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}
              >
                2
              </div>
              <span className="text-xs font-semibold sm:text-sm">Details</span>
            </div>
          </div>

          {/* Step 1 — Requester */}
          {step === "requester" && (
            <div className="space-y-4 sm:space-y-6">
              {/* User Search */}
              <div className="space-y-2">
                <Label>Search for requester</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* User List */}
              <div className="space-y-2">
                {filteredUsers.map(user => (
                  <button
                    key={user.id}
                    onClick={() => setSelectedUser(user)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors sm:gap-4 sm:p-4",
                      selectedUser?.id === user.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50"
                    )}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 sm:h-10 sm:w-10">
                      <User className="h-4 w-4 text-primary sm:h-5 sm:w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">
                        {user.name}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                    <div className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:flex">
                      <Building className="h-3.5 w-3.5 shrink-0" />
                      <span className="max-w-[120px] truncate">{user.company}</span>
                    </div>
                    {selectedUser?.id === user.id && (
                      <CheckCircle className="h-4 w-4 shrink-0 text-primary sm:h-5 sm:w-5" />
                    )}
                  </button>
                ))}
              </div>

              <Button
                onClick={() => setStep("details")}
                disabled={!selectedUser}
                className="w-full"
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step 2 — Details */}
          {step === "details" && (
            <div className="space-y-4 sm:space-y-6">
              {/* Selected User banner */}
              {selectedUser && (
                <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3 sm:gap-4 sm:p-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 sm:h-10 sm:w-10">
                    <User className="h-4 w-4 text-primary sm:h-5 sm:w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {selectedUser.name}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {selectedUser.email}
                    </p>
                  </div>
                </div>
              )}

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  placeholder="Brief description of the issue..."
                  value={formData.subject}
                  maxLength={SUBJECT_MAX_LENGTH}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      subject: clampTicketSubject(e.target.value),
                    })
                  }
                  onPaste={e => handleSubjectInputPaste(e, SUBJECT_MAX_LENGTH)}
                />
                <p className="text-right text-xs text-muted-foreground">
                  {formData.subject.length} / {SUBJECT_MAX_LENGTH}
                </p>
              </div>

              {/* Category & Priority — side-by-side from sm */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={value =>
                      setFormData({ ...formData, category: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={value =>
                      setFormData({ ...formData, priority: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="P1">P1 - Critical</SelectItem>
                      <SelectItem value="P2">P2 - High</SelectItem>
                      <SelectItem value="P3">P3 - Medium</SelectItem>
                      <SelectItem value="P4">P4 - Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Provide detailed information about the issue..."
                  value={formData.description}
                  onChange={e =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="min-h-[120px] resize-none sm:min-h-[150px]"
                />
              </div>

              {/* Submit buttons */}
              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate(`${SD_BASE}/`)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleSubmit}
                  disabled={
                    !formData.subject ||
                    !formData.category ||
                    !formData.description ||
                    isSubmitting
                  }
                >
                  {isSubmitting ? "Creating..." : "Create Ticket"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default NewTicket;
